//
// # Draw Route Mode
//
// Interactive Manhattan route drawing mode for layout views.
// Press 'R' to enter. Click to place points (grid-snapped, Manhattan-constrained).
// Double-click or Enter to commit the route. Escape to cancel.
//

// Local Imports
import { schema } from "GenAlphaSchema";
import { model, graphics, layout, Point } from "GenAlphaModel";
import type { InteractionHost } from "../../interaction_host";
import { ModeHandler, Pan, DragZoom } from "../../modes";
import type { DrawTo } from "DrawCore/src/draw";
import * as style from "DrawCore/src/style";
import * as changes from "DrawCore/src/changes";
import { Keys } from "../../keys";
import { LayoutContent } from "./content";
import { RouteEntity, SegmentEntity } from "DrawCore/src/content/layout/route";
import { routingGridSnap } from "DrawCore/src/content/layout/stack";

// Drag threshold in page pixels: below this a mousedown+mouseup is a plain click,
// not a box-select drag. Prevents bbox-select from interfering with double-click.
const BBOX_DRAG_THRESHOLD_PX = 5;

// Visual style for the in-progress route preview
const PREVIEW_COLOR = style.Color.new(255, 255, 0, 1.0); // Yellow
const previewLineStyle: style.ShapeStyle = {
  cap: "round",
  join: "round",
  linewidth: 2,
  fill: style.Special.None,
  stroke: PREVIEW_COLOR,
};
// Translucent fill for previewing a segment's actual drawn width while routing.
const previewWidthFillStyle: style.ShapeStyle = {
  cap: "round",
  join: "round",
  linewidth: 0,
  fill: style.Color.new(255, 255, 0, 0.2),
  stroke: style.Special.None,
};

// Get the lowest metal layer with a given preferred direction from the stack.
// Returns null if no stack or no matching layer is found.
function getLayerByDirection(
  content: LayoutContent,
  dir: "horiz" | "vert"
): layout.MetalLayer | null {
  const metals = content.stack?.metalLayers;
  if (!metals) return null;
  return metals.find((l) => l.direction === dir) ?? null;
}

// Snap a point to the routing grid, applying Manhattan constraint relative to the last placed point.
function snapToGrid(
  editor: InteractionHost,
  relativeTo: model.Point
): model.Point {
  return editor.grid.nearestManhattan(editor.uiState.mousePos.sch, relativeTo);
}

// Find the path index of a segment within its parent route.
// Returns index i such that path[i] → path[i+1] is the given segment.
// Matches by coordinate equality (reference equality also works since build()
// stores direct references, but coord equality is more robust after updates).
// Returns null if not found.
function findSegmentPathIndex(
  route: RouteEntity,
  seg: SegmentEntity,
  hitPoint: model.Point
): number | null {
  const { path } = route.data;
  const [p0, p1] = seg.data.points;
  // Fast path: the rendered segment maps 1:1 to a single path pair (the common
  // case — a segment that was not merged with a neighbor).
  for (let i = 0; i < path.length - 1; i++) {
    const rp0 = path[i].point;
    const rp1 = path[i + 1].point;
    // Skip via segments (same point, different layer)
    if (rp0.x === rp1.x && rp0.y === rp1.y) continue;
    if (rp0.x === p0.x && rp0.y === p0.y && rp1.x === p1.x && rp1.y === p1.y) {
      return i;
    }
  }
  // Merged case: build() collapses collinear, same-layer, same-width segments
  // into one rendered SegmentEntity spanning multiple path pairs, so the exact
  // endpoint match above fails. Disambiguate by which path sub-segment actually
  // contains the click — this is what lets each sub-segment's width be edited
  // (once widths differ they no longer merge, and the fast path applies again).
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i];
    const rp0 = a.point;
    const rp1 = path[i + 1].point;
    if (rp0.x === rp1.x && rp0.y === rp1.y) continue; // via
    const tol = Math.max(layout.effectiveRouteWidth(a.width, a.layer) / 2, 1);
    if (rp0.y === rp1.y) {
      const xmin = Math.min(rp0.x, rp1.x);
      const xmax = Math.max(rp0.x, rp1.x);
      if (
        Math.abs(hitPoint.y - rp0.y) <= tol &&
        hitPoint.x >= xmin - tol &&
        hitPoint.x <= xmax + tol
      ) {
        return i;
      }
    } else if (rp0.x === rp1.x) {
      const ymin = Math.min(rp0.y, rp1.y);
      const ymax = Math.max(rp0.y, rp1.y);
      if (
        Math.abs(hitPoint.x - rp0.x) <= tol &&
        hitPoint.y >= ymin - tol &&
        hitPoint.y <= ymax + tol
      ) {
        return i;
      }
    }
  }
  return null;
}

// A route target on a pin: one per pin shape, at the shape's centre on its
// layer. A pin with several shapes is several targets.
interface PinLoc {
  point: model.Point;
  layer: layout.MetalLayer;
}

// The targets of `pin`'s shapes, moved by `xform` when given. A shape whose
// layer is not a metal of the stack is no route target.
function pinTargets(
  content: LayoutContent,
  pin: layout.LayoutPin,
  xform?: model.Xform
): PinLoc[] {
  const out: PinLoc[] = [];
  for (const { layer, shape } of pin.shapes) {
    const metal = content.stack?.metalLayers.find((m) => m.layer === layer);
    if (metal === undefined) continue;
    const c = schema.pinShapeCenter(shape);
    out.push({
      point: xform ? model.Xform.transform(xform, c) : c,
      layer: metal,
    });
  }
  return out;
}

// Each instance pin, with its world-space targets.
// Mirrors InstanceEntity.collectBboxes: handles scalar, segments, and array kinds.
function collectInstancePins(
  content: LayoutContent
): Array<{ inst: string; pin: string; targets: PinLoc[] }> {
  const out: Array<{ inst: string; pin: string; targets: PinLoc[] }> = [];
  const { implEntity } = content.layoutEntity;
  if (!implEntity) return out;
  for (const instance of implEntity.instances.values()) {
    const inst = instance.data.name;
    const { inner } = instance;
    if (inner.kind === "scalar") {
      const { layout: lay } = inner.drawer.data.target;
      for (const pin of lay.abs.pins) {
        const targets = pinTargets(content, pin, inner.drawer.xform);
        out.push({ inst, pin: pin.name, targets });
      }
    } else if (inner.kind === "segments" || inner.kind === "array") {
      for (const seg of inner.drawer.segments) {
        if (seg.scalarEntity) {
          const { layout: lay } = seg.scalarEntity.data.target;
          for (const pin of lay.abs.pins) {
            const targets = pinTargets(content, pin, seg.scalarEntity.xform);
            out.push({ inst, pin: pin.name, targets });
          }
        }
      }
    }
  }
  return out;
}

// Every world-space pin target: the layout's own pins and its instances'.
function collectPinTargets(content: LayoutContent): PinLoc[] {
  const out: PinLoc[] = [];
  for (const pin of content.layoutEntity.absEntity.pins.values())
    out.push(...pinTargets(content, pin.data));
  for (const { targets } of collectInstancePins(content)) out.push(...targets);
  return out;
}

// Snap radius for pin gravity in screen pixels.
const PIN_SNAP_RADIUS_PX = 30;

// If rawPos (raw grid-snapped cursor) is within PIN_SNAP_RADIUS_PX of any
// instance pin, return that pin's nearest routing grid point (may be off-Manhattan
// from the last placed point — caller handles the L-bend).
// Returns null when no pin is within range.
function snapWithPinGravity(
  editor: InteractionHost,
  content: LayoutContent,
  rawPos: model.Point
): model.Point | null {
  const scale = editor.canvas.worldTransforms.schToPage.scale;
  const r2 = (PIN_SNAP_RADIUS_PX / scale) ** 2;
  let best: model.Point | null = null;
  let bestD2 = r2;

  const check = (loc: { x: number; y: number }): void => {
    const gridSnapped = editor.grid.nearestPoint(loc);
    const dx = gridSnapped.x - rawPos.x;
    const dy = gridSnapped.y - rawPos.y;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = gridSnapped;
    }
  };

  for (const { point } of collectPinTargets(content)) check(point);

  return best;
}

// Return the metal layer of a pin whose grid-snapped location equals `point`,
// or null if no pin sits there. Used at commit to detect that a route endpoint
// (which was gravity-snapped to a pin's grid point) lands on a pin, so a
// junction via can be added when the pin is on a different layer (bug B3).
function pinLayerAtPoint(
  editor: InteractionHost,
  content: LayoutContent,
  point: model.Point
): layout.MetalLayer | null {
  const at = (loc: { x: number; y: number }): boolean => {
    const g = editor.grid.nearestPoint(loc);
    return g.x === point.x && g.y === point.y;
  };
  for (const { point: loc, layer } of collectPinTargets(content))
    if (at(loc)) return layer;
  return null;
}

// A group of flying line targets for a single net.
interface FlyingLineGroup {
  net: string;
  targets: model.Point[];
}

// Distinct colors for flying lines, one per net (cycled if more nets than colors).
const FLYING_LINE_COLORS: Array<[number, number, number]> = [
  [0, 200, 255], // cyan
  [255, 150, 0], // orange
  [0, 255, 100], // green
  [255, 80, 200], // pink
  [180, 100, 255], // purple
  [255, 255, 0], // yellow
];

// Given a snapped anchor point (from pin gravity), find all other pins on the
// same net(s) and return their world-space locations grouped by net.
// Tolerance is used because grid-snapping may shift the anchor slightly from the pin's exact loc.
function findConnectedPinLocations(
  editor: InteractionHost,
  content: LayoutContent,
  anchorPt: model.Point
): FlyingLineGroup[] {
  const { implEntity } = content.layoutEntity;
  if (!implEntity) return [];

  const conns = implEntity.data.conns;
  if (!conns || conns.length === 0) return [];

  const scale = editor.canvas.worldTransforms.schToPage.scale;
  const tol2 = (PIN_SNAP_RADIUS_PX / scale) ** 2;

  // Build lookup: pin name/inst:port → world-space location
  type PinId =
    | { kind: "pin"; pin: string }
    | { kind: "inst"; inst: string; port: string };
  const pinLocs: Array<{ id: PinId; loc: model.Point }> = [];

  // Layout-level pins
  for (const pin of content.layoutEntity.absEntity.pins.values()) {
    for (const { point } of pinTargets(content, pin.data)) {
      pinLocs.push({ id: { kind: "pin", pin: pin.data.name }, loc: point });
    }
  }

  // Instance pins
  for (const { inst, pin, targets } of collectInstancePins(content)) {
    for (const { point } of targets) {
      pinLocs.push({ id: { kind: "inst", inst, port: pin }, loc: point });
    }
  }

  // Find which pin the anchor snapped to (closest within tolerance)
  let matchedId: PinId | null = null;
  let bestD2 = tol2;
  for (const { id, loc } of pinLocs) {
    const gridLoc = editor.grid.nearestPoint(loc);
    const dx = gridLoc.x - anchorPt.x;
    const dy = gridLoc.y - anchorPt.y;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD2) {
      bestD2 = d2;
      matchedId = id;
    }
  }
  if (!matchedId) return [];

  // Find which nets this pin belongs to
  const matchedNets: string[] = [];
  for (const conn of conns) {
    for (const port of conn.ports) {
      if (
        matchedId.kind === "pin" &&
        port.kind === "pin" &&
        port.pin === matchedId.pin
      ) {
        matchedNets.push(conn.net);
      } else if (
        matchedId.kind === "inst" &&
        port.kind === "inst" &&
        port.inst === matchedId.inst &&
        port.port === matchedId.port
      ) {
        matchedNets.push(conn.net);
      }
    }
  }
  if (matchedNets.length === 0) return [];

  const matchKey =
    matchedId.kind === "pin"
      ? `pin:${matchedId.pin}`
      : `inst:${matchedId.inst}:${matchedId.port}`;

  // Build a map from pin key → world-space locations for fast lookup.
  // Use an array because array instances can have multiple pins with the same key.
  const locsByKey = new Map<string, model.Point[]>();
  for (const { id, loc } of pinLocs) {
    const key =
      id.kind === "pin" ? `pin:${id.pin}` : `inst:${id.inst}:${id.port}`;
    const existing = locsByKey.get(key);
    if (existing) {
      existing.push(loc);
    } else {
      locsByKey.set(key, [loc]);
    }
  }

  // Collect targets grouped by net
  const result: FlyingLineGroup[] = [];
  for (const netName of matchedNets) {
    const conn = conns.find((c) => c.net === netName);
    if (!conn) continue;
    const targets: model.Point[] = [];
    for (const port of conn.ports) {
      const key =
        port.kind === "pin"
          ? `pin:${port.pin}`
          : `inst:${port.inst}:${port.port}`;
      if (key === matchKey) continue; // skip the source pin
      const locs = locsByKey.get(key);
      if (locs) targets.push(...locs);
    }
    if (targets.length > 0) {
      result.push({ net: netName, targets });
    }
  }
  return result;
}

// Compute the H-first L-bend intermediate point between two grid points.
// Returns null when from and to are already on the same horizontal line (no bend needed).
function lBendPoint(from: model.Point, to: model.Point): model.Point | null {
  if (from.y === to.y) return null; // already horizontal — no bend
  return { x: to.x, y: from.y };
}

// Draw a diamond marker to indicate the cursor is snapped to a pin.
function drawPinSnapIndicator(
  to: DrawTo,
  point: model.Point,
  size: number,
  lineStyle: style.ShapeStyle
): void {
  to.path(
    graphics.Path.fromPartial({
      points: [
        { x: point.x, y: point.y - size },
        { x: point.x + size, y: point.y },
        { x: point.x, y: point.y + size },
        { x: point.x - size, y: point.y },
        { x: point.x, y: point.y - size },
      ],
    }),
    lineStyle
  );
}

// Draw a crosshair marker at the given point (in world coordinates)
function drawCrosshair(
  to: DrawTo,
  point: model.Point,
  size: number,
  lineStyle: style.ShapeStyle
): void {
  to.path(
    graphics.Path.fromPartial({
      points: [
        { x: point.x - size, y: point.y },
        { x: point.x + size, y: point.y },
      ],
    }),
    lineStyle
  );
  to.path(
    graphics.Path.fromPartial({
      points: [
        { x: point.x, y: point.y - size },
        { x: point.x, y: point.y + size },
      ],
    }),
    lineStyle
  );
}

//
// # DrawRoute Mode
//
// Single mode class that handles the full route drawing flow:
// - First mouseUp places the anchor point
// - Subsequent mouseUps add segments
// - Mouse move updates rubber-band preview
// - Double-click or Enter commits the route
// - Escape cancels
//
export class DrawRoute extends ModeHandler {
  // Whether the route is currently being drawn (points placed).
  get isDrawing(): boolean {
    return this.points.length > 0;
  }

  // Metal layers from the stack, for the layer picker menu.
  get metalLayers(): ReadonlyArray<layout.MetalLayer> {
    return this.content.stack?.metalLayers ?? [];
  }

  // Index of the active layer in the metal stack, or -1.
  get activeLayerIndex(): number {
    const metals = this.metalLayers;
    return metals.findIndex(
      (l) =>
        l.layer === this.activeLayer.layer &&
        l.purpose === this.activeLayer.purpose
    );
  }

  // Flying line targets: pins connected to the starting pin, grouped by net.
  // Populated when the first anchor is placed with pin gravity; cleared on commit/cancel.
  private flyingLineTargets: FlyingLineGroup[] = [];
  // Toggle for flying lines visibility. Persists across route commits within a session.
  private showFlyingLines = true;

  // Placed points so far (at least 1 after the first click)
  private readonly points: model.Point[] = [];
  // Current rubber-band endpoint (updated on mouse move)
  private cursor: model.Point;
  // Layers looked up from PDK stack by preferred direction
  private readonly horizLayer: layout.MetalLayer;
  private readonly vertLayer: layout.MetalLayer;
  // Grid settings saved on enter so we can restore them on cleanup
  private readonly savedGridSettings: { snapX: number; snapY: number };
  // Active metal layer for drawing. Starts on the vert-preferred layer and cycles
  // through the stack's metalLayers on `l` key press. Persists across route commits
  // within a single DrawRoute session (reset only on exit and re-enter).
  private activeLayer: layout.MetalLayer;
  // Explicit layer per placed point, kept in parallel with this.points.
  // null = use direction-based assignment; non-null = layer pinned by an `l` press.
  // Once a non-null entry is seen in commit(), it propagates forward to subsequent
  // null entries — so all points after a via inherit the switched-to layer.
  private readonly pointLayers: Array<layout.MetalLayer | null> = [];
  // Explicit width (nm) of the segment LEAVING each placed point, kept in
  // parallel with this.points (outgoing-segment convention). null = layer
  // default. Captured as currentWidth when the point is placed and updated for
  // the in-progress segment when currentWidth changes mid-draw. The last point's
  // entry is orphan (no outgoing segment). Enables per-segment width while drawing.
  private readonly pointWidths: Array<number | null> = [];
  // Non-null when cursor is gravity-snapped to a pin; holds the pin's grid point.
  // Drives L-shaped rubber-band preview and 2-point commit on mouseUp.
  private gravityTarget: model.Point | null = null;
  // Route clipboard for Cmd+C / Cmd+V copy-paste within DrawRoute mode.
  // Stores full route data (net + path) for each copied route.
  private routeClipboard: layout.Route[] = [];
  // True once the user has pressed l/Shift+L to manually pick a layer.
  // Enables the preferred-direction check when placing subsequent points.
  private layerUserSelected = false;
  // Pending box-select: set on mousedown on empty space, committed to RouteBoxSelect
  // only after the mouse drags past BBOX_DRAG_THRESHOLD_PX. Cleared on mouseup (plain click).
  // This deferred entry prevents bbox-select from visually firing during a double-click.
  private pendingBboxAnchor: {
    sch: model.Point;
    page: model.Point;
    additive: boolean;
  } | null = null;
  // Pending route interaction: set on an idle left-mousedown over an existing
  // route, resolved later so the gesture doesn't pre-empt starting a route.
  // Resolves to: select (plain click, handleMouseUp), move/extend (drag past
  // threshold, handleMouseMove), or discarded (a double-click starts a route
  // here instead — handleDoubleClick). Mirrors pendingBboxAnchor's deferral so a
  // pin that already has a route attached stays routable (bug B2).
  private pendingRouteInteraction: {
    child: RouteEntity;
    hit: SegmentEntity;
    page: model.Point;
    shift: boolean;
  } | null = null;
  // Width for the route currently being drawn. null means "use layer default".
  // Persists across route commits within a single DrawRoute session.
  private currentWidth: number | null = null;

  private constructor(
    private readonly editor: InteractionHost,
    private readonly content: LayoutContent,
    horizLayer: layout.MetalLayer,
    vertLayer: layout.MetalLayer
  ) {
    super();
    this.cursor = { ...editor.uiState.mousePos.sch };
    this.horizLayer = horizLayer;
    this.vertLayer = vertLayer;
    // Save current grid snap and switch to the routing grid. Tracks are
    // per-layer and per-axis: the vertical layer's pitch constrains X, the
    // horizontal layer's pitch constrains Y (see routingGridSnap). Snapping
    // both axes to a single min-of-both pitch (the old behavior) put manual
    // routes on a different lattice than the auto-router whenever the two
    // pitches differ (e.g. GF12). enter() has already validated that both
    // layers exist with positive pitch, so this is non-null in practice; keep
    // the current grid as a defensive fallback if that ever changes.
    const { snapX, snapY } = editor.grid.settings;
    this.savedGridSettings = { snapX, snapY };
    const routingSnap = routingGridSnap(content.stack?.metalLayers ?? []);
    if (routingSnap) {
      editor.grid.updateSettings(routingSnap);
    }
    this.activeLayer = vertLayer;
    this.updateStatusBar();
  }

  static enter(editor: InteractionHost): DrawRoute | null {
    const content = editor.content;
    if (!(content instanceof LayoutContent)) return null;
    // The routing grid is derived entirely from the PDK stack's metal layers.
    // Refuse to enter routing mode (rather than snap to a bogus grid) if that
    // information is missing or malformed — a missing stack, no layer in one of
    // the two directions, or a non-positive pitch all leave us unable to place
    // routes on tracks that line up with the auto-router.
    if (!content.stack) {
      editor.overlays.setStatusMessage(
        "Cannot draw route: layout has no PDK metal stack loaded",
        "draw-route"
      );
      return null;
    }
    const horizLayer = getLayerByDirection(content, "horiz");
    const vertLayer = getLayerByDirection(content, "vert");
    if (!horizLayer || !vertLayer) {
      editor.overlays.setStatusMessage(
        "Cannot draw route: stack must have both horiz and vert metal layers",
        "draw-route"
      );
      return null;
    }
    if (horizLayer.pitch <= 0 || vertLayer.pitch <= 0) {
      editor.overlays.setStatusMessage(
        "Cannot draw route: metal stack has an invalid (non-positive) pitch",
        "draw-route"
      );
      return null;
    }
    const mode = new DrawRoute(editor, content, horizLayer, vertLayer);
    editor.setMode(mode);
    return mode;
  }

  // On mouse down, check if the user clicked an existing route segment.
  // Routes are only interactive in DrawRoute mode (includeRoutes=true).
  // Left-click: select + drag. Right-click: show route context menu.
  override handleMouseDown = (e: MouseEvent): unknown => {
    const { editor, content } = this;

    // Pass through pan/zoom gestures so they work in route mode too.
    // Pass `this` as returnMode so Pan/DragZoom return to DrawRoute on exit.
    if (e.button === 1 && e.ctrlKey) {
      editor.modeHandler = DragZoom.start(editor, this);
      return;
    }
    if (e.button === 1) {
      editor.modeHandler = Pan.start(editor, this);
      return;
    }
    if (e.metaKey) {
      editor.modeHandler = Pan.start(editor, this);
      return;
    }

    // Part A (bug B2): while drawing, a left-click is always a routing gesture
    // (place/land a point) — never route selection, even when it lands on an
    // existing route or a pin that has a route attached. handleMouseUp adds the
    // point (with pin gravity) and handleDoubleClick finishes. This mirrors how
    // mid-draw left-clicks on empty space already behave.
    if (this.points.length > 0 && e.button === 0) return null;

    const hit = content.whatdWeHit(editor.uiState.mousePos, true);
    if (!(hit instanceof SegmentEntity)) {
      // Right-click mid-draw: show layer picker context menu
      if (this.points.length > 0 && e.button === 2) {
        e.preventDefault();
        e.stopPropagation();
        editor.layerPickerMenu.showForDrawRoute(this, e.pageX, e.pageY);
        return null;
      }
      // Clicking empty space while not drawing: record anchor for a deferred
      // box-select. We don't enter RouteBoxSelect immediately so that a
      // double-click (whose first mousedown lands here) doesn't show a
      // box-select rectangle before handleDoubleClick fires.
      if (this.points.length === 0 && e.button === 0) {
        this.pendingBboxAnchor = {
          sch: { ...editor.uiState.mousePos.sch },
          page: { ...editor.uiState.mousePos.page },
          additive: e.shiftKey,
        };
      }
      return null;
    }
    const child = content.resolveChild(hit);
    if (!(child instanceof RouteEntity)) return null;

    // Right-click: show route context menu
    if (e.button === 2) {
      e.preventDefault();
      e.stopPropagation();
      content.select(child);
      const segIdx = findSegmentPathIndex(
        child,
        hit,
        editor.uiState.mousePos.sch
      );
      editor.routeContextMenu.show(child, e.pageX, e.pageY, segIdx);
      return null;
    }

    // Part B (bug B2): left-click on an existing route while idle. DEFER the
    // interaction instead of selecting / entering a move immediately, so a
    // double-click can START a route here (a pin with a route attached must stay
    // routable — see handleDoubleClick). Resolution: plain click → select
    // (handleMouseUp); drag past threshold → move/extend (handleMouseMove);
    // double-click → discarded, route starts. (Mid-draw left-clicks already
    // returned above, so this is idle-only.)
    if (e.button === 0) {
      this.pendingRouteInteraction = {
        child,
        hit,
        page: { ...editor.uiState.mousePos.page },
        shift: e.shiftKey,
      };
      return null;
    }

    return null;
  };

  // Begin a drag on an existing route (select + the right move/extend mode).
  // Called when a deferred route interaction is promoted to a drag past the
  // threshold (handleMouseMove). Mirrors the pre-defer mousedown left-click logic.
  private beginRouteDrag = (
    child: RouteEntity,
    hit: SegmentEntity,
    shift: boolean
  ): unknown => {
    const { editor, content } = this;
    // Group drag: clicked route already part of a multi-route selection.
    if (!shift && content.selection.has(child) && content.selection.size > 1) {
      const routes: RouteEntity[] = [];
      for (const ent of content.selection) {
        if (ent instanceof RouteEntity) routes.push(ent);
      }
      if (routes.length > 1) {
        return MoveRouteGroup.enter(editor, content, routes, this);
      }
    }
    content.select(child);
    if (shift) {
      return MoveRoute.enter(editor, content, child, this);
    }
    // Near a route endpoint → extend/shrink along axis.
    const endpointHit = hitTestRouteEndpoint(editor, child);
    if (endpointHit) {
      return ExtendRouteEnd.enter(
        editor,
        content,
        child,
        endpointHit.which,
        endpointHit.axis,
        this
      );
    }
    const segIdx = findSegmentPathIndex(
      child,
      hit,
      editor.uiState.mousePos.sch
    );
    if (segIdx !== null) {
      return MoveSegment.enter(
        editor,
        content,
        child,
        segIdx,
        hit.data.dir,
        this
      );
    }
    // Fallback: whole-route drag if segment index can't be resolved.
    return MoveRoute.enter(editor, content, child, this);
  };

  // Add a point on mouse up, but only when already drawing (points.length > 0).
  // Starting a new route requires a double-click on empty space.
  override handleMouseUp = (): unknown => {
    // Plain click on empty space (mouse didn't drag past threshold): deselect.
    if (this.pendingBboxAnchor !== null) {
      const { sch, additive } = this.pendingBboxAnchor;
      this.pendingBboxAnchor = null;
      const zeroBbox = model.Bbox.fromTwoPoints([sch, sch]);
      this.content.selectAllRoutesInBbox(zeroBbox, additive);
      return this.editor.requestRedraw();
    }
    // Plain click on an existing route (no drag past threshold): select it.
    if (this.pendingRouteInteraction !== null) {
      const { child } = this.pendingRouteInteraction;
      this.pendingRouteInteraction = null;
      this.content.select(child);
      return this.editor.requestRedraw();
    }
    if (this.points.length === 0) return null;
    const { editor } = this;
    const relativeTo = this.points[this.points.length - 1];
    if (this.gravityTarget !== null) {
      // Gravity active: commit L-bend + pin grid point.
      const bend = lBendPoint(relativeTo, this.gravityTarget);
      if (bend !== null) {
        this.points.push({ ...bend });
        this.pointLayers.push(null); // direction-based (or inherits explicit layer in commit)
        this.pointWidths.push(this.currentWidth);
      }
      this.points.push({ ...this.gravityTarget });
      this.pointLayers.push(null);
      this.pointWidths.push(this.currentWidth);
      this.cursor = { ...this.gravityTarget };
    } else {
      const snapped = snapToGrid(editor, relativeTo);
      // Preferred-layer check: once the user has manually switched layers,
      // warn and block if the new segment's direction doesn't match the active
      // layer's preferred direction (e.g. routing vertically on a horiz layer).
      if (this.layerUserSelected) {
        const dx = Math.abs(snapped.x - relativeTo.x);
        const dy = Math.abs(snapped.y - relativeTo.y);
        if (dx > 0 || dy > 0) {
          const segDir = dx >= dy ? "horiz" : "vert";
          if (this.activeLayer.direction !== segDir) {
            const label = this.layerLabel(this.activeLayer);
            this.editor.overlays.setStatusMessage(
              `${label} is a ${this.activeLayer.direction} layer — rotate segment or switch layers (l / Shift+L)`,
              "draw-route"
            );
            return null;
          }
        }
      }
      this.points.push({ ...snapped });
      this.pointLayers.push(null);
      this.pointWidths.push(this.currentWidth);
      this.cursor = { ...snapped };
    }
    return editor.requestRedraw();
  };

  // Update rubber-band cursor on mouse move
  override handleMouseMove = (): unknown => {
    const { editor, content } = this;
    // If mouse dragged past threshold while a box-select is pending, start it now.
    if (this.pendingBboxAnchor !== null) {
      const currPage = editor.uiState.mousePos.page;
      const dx = currPage.x - this.pendingBboxAnchor.page.x;
      const dy = currPage.y - this.pendingBboxAnchor.page.y;
      if (dx * dx + dy * dy > BBOX_DRAG_THRESHOLD_PX * BBOX_DRAG_THRESHOLD_PX) {
        const { sch, additive } = this.pendingBboxAnchor;
        this.pendingBboxAnchor = null;
        RouteBoxSelect.enter(editor, content, this, sch, additive);
      }
      return null;
    }
    // If a deferred route interaction drags past threshold, promote it to a
    // move/extend now (preserves click-drag editing of existing routes).
    if (this.pendingRouteInteraction !== null) {
      const currPage = editor.uiState.mousePos.page;
      const dx = currPage.x - this.pendingRouteInteraction.page.x;
      const dy = currPage.y - this.pendingRouteInteraction.page.y;
      if (dx * dx + dy * dy > BBOX_DRAG_THRESHOLD_PX * BBOX_DRAG_THRESHOLD_PX) {
        const { child, hit, shift } = this.pendingRouteInteraction;
        this.pendingRouteInteraction = null;
        return this.beginRouteDrag(child, hit, shift);
      }
      return null;
    }
    if (this.points.length === 0) return null;
    const relativeTo = this.points[this.points.length - 1];
    const rawSnapped = editor.grid.nearestPoint(editor.uiState.mousePos.sch);
    this.gravityTarget = snapWithPinGravity(editor, content, rawSnapped);
    this.cursor = this.gravityTarget ?? snapToGrid(editor, relativeTo);
    return editor.requestRedraw();
  };

  // Double-click on empty space: start a new route at the snapped position.
  // Double-click while drawing: commit at the double-click position.
  //
  // A double-click fires mouseUp #1 → mouseUp #2 → dblclick. In the normal
  // case each mouseUp snapshots the cursor and pushes it, leaving a duplicate
  // at the end (last === cursor). We pop one copy and commit.
  //
  // When the preferred-layer guard is active and the double-click direction
  // mismatches the active layer, both mouseUps are blocked — nothing is pushed
  // and last !== cursor. In that case we push cursor once (no pop needed) so
  // the final segment endpoint is not lost before commit.
  override handleDoubleClick = (): unknown => {
    const { editor, content } = this;
    // A double-click pre-empts any deferred single-click route interaction.
    this.pendingRouteInteraction = null;
    if (this.points.length === 0) {
      // Pin gravity first: start on a pin even when a route is attached to it
      // (pin wins — bug B2 Symptom A). Only refuse to start on a *bare* route
      // (no pin) — that's a route-edit context, not a route start.
      const snapped = editor.grid.nearestPoint(editor.uiState.mousePos.sch);
      const gravity = snapWithPinGravity(editor, content, snapped);
      if (gravity === null) {
        const hit = content.whatdWeHit(editor.uiState.mousePos, true);
        if (hit instanceof SegmentEntity) return null;
      }
      const anchor = gravity ?? snapped;
      // Clear any transient selection left by the deferred single-click
      // resolution on the first mouseUp of this double-click.
      content.deselect();
      this.points.push({ ...anchor });
      this.pointLayers.push(null);
      this.pointWidths.push(this.currentWidth);
      this.cursor = { ...anchor };
      // Populate flying line targets when starting from a pin
      if (gravity) {
        this.flyingLineTargets = findConnectedPinLocations(
          editor,
          content,
          anchor
        );
      } else {
        this.flyingLineTargets = [];
      }
      return editor.requestRedraw();
    }
    // Already drawing: ensure the double-click endpoint is in the path.
    if (this.points.length > 1) {
      const last = this.points[this.points.length - 1];
      if (last.x === this.cursor.x && last.y === this.cursor.y) {
        // Normal: mouseUps pushed the cursor position twice — pop the duplicate.
        this.points.pop();
        this.pointLayers.pop();
        this.pointWidths.pop();
      } else {
        // Preferred-layer guard blocked both mouseUps — cursor was never pushed.
        // Push it once so the final segment is included in the committed route.
        this.points.push({ ...this.cursor });
        this.pointLayers.push(null);
        this.pointWidths.push(this.currentWidth);
      }
    }
    return this.commit();
  };

  override handleKey = (e: KeyboardEvent): unknown => {
    switch (e.key) {
      case Keys.Enter: {
        // If gravity is active, push the snapped endpoint before committing —
        // Enter skips handleMouseDown so gravityTarget is never auto-pushed.
        if (this.gravityTarget !== null && this.points.length > 0) {
          const relativeTo = this.points[this.points.length - 1];
          const bend = lBendPoint(relativeTo, this.gravityTarget);
          if (bend !== null) {
            this.points.push({ ...bend });
            this.pointLayers.push(null);
            this.pointWidths.push(this.currentWidth);
          }
          this.points.push({ ...this.gravityTarget });
          this.pointLayers.push(null);
          this.pointWidths.push(this.currentWidth);
        }
        return this.commit();
      }
      case Keys.Escape:
        // Handled by abort() above (editor.ts routes Escape there directly).
        return this.abort();
      case Keys.R:
        // Shift+R always exits route mode, regardless of drawing state
        return this.cancel();
      case Keys.c:
        // Cmd+C / Ctrl+C: copy selected routes to clipboard (idle only)
        if ((e.metaKey || e.ctrlKey) && this.points.length === 0)
          return this.copySelectedRoutes();
        return null;
      case Keys.v:
        // Cmd+V / Ctrl+V: paste clipboard routes at a small offset (idle only)
        if ((e.metaKey || e.ctrlKey) && this.points.length === 0)
          return this.pasteRoutes();
        return null;
      case Keys.u:
        // U: cycle active layer UP.
        return this.cycleLayer(+1);
      case Keys.d:
        // D: cycle active layer DOWN.
        return this.cycleLayer(-1);
      case Keys.a:
        // `A`: enter auto-route sub-mode (idle only — not mid-draw).
        // First click selects source pin, second click selects destination and commits.
        if (this.points.length === 0)
          return AutoRoute.enter(this.editor, this.content, this);
        return null;
      case "n":
        // N: toggle flying lines visibility
        this.showFlyingLines = !this.showFlyingLines;
        this.editor.overlays.setStatusMessage(
          this.showFlyingLines ? "Flying lines ON" : "Flying lines OFF",
          "flying-lines"
        );
        // Auto-clear the message after a short delay
        setTimeout(() => {
          this.editor.overlays.clearStatusMessage("flying-lines");
          this.updateStatusBar();
        }, 1500);
        return this.editor.requestRedraw();
      case Keys.Delete:
      case Keys.Backspace:
        // Delete selected routes without exiting route mode (only when not mid-draw).
        // We cannot call content.deleteSelected() here because that calls goUiIdle(),
        // which would exit DrawRoute and drop the user back to idle mode.
        if (this.points.length === 0) return this.deleteSelectedRoutes();
        return null;
      case "+":
      case "=":
        // Increase width by one manufacturing-grid step (mid-draw or selection).
        return this.adjustWidth(1);
      case "-":
        // Decrease width by one manufacturing-grid step (mid-draw or selection).
        return this.adjustWidth(-1);
      case "w":
      case "W":
        // Open the precise width dialog for the route being drawn (mid-draw only).
        if (this.points.length > 0) return this.openWidthDialog();
        return null;
      default:
        return null;
    }
  };

  // One manufacturing-grid step for width adjustment, from the PDK (falls back
  // to 1nm when the PDK doesn't specify a grid).
  private get widthStepNm(): number {
    const grid = this.content.pdk?.manufacturingGrid ?? 1;
    return grid > 0 ? grid : 1;
  }

  // Switch the active layer and keep the pending custom draw width valid for it.
  // A default (null) width follows the new layer's default, so leave it null;
  // a custom width is re-normalized (clamped/snapped) against the new layer so
  // switching to a narrower/wider layer mid-draw can't commit an out-of-bounds
  // width on the new-layer segments.
  private setActiveLayer = (layer: layout.MetalLayer): void => {
    this.activeLayer = layer;
    if (this.currentWidth !== null) {
      this.currentWidth = layout.normalizeRouteWidth(
        this.currentWidth,
        layer,
        this.widthStepNm
      );
    }
  };

  // Adjust width by `direction` grid steps (+1 wider, -1 narrower).
  // Mid-draw: adjusts the uniform width the new route will commit with.
  // Idle with routes selected: bumps every segment of each selected route by
  // one step, each segment against its own layer (handles mixed-layer routes
  // and pre-existing per-segment widths, preserving their relative differences).
  private adjustWidth = (direction: number): unknown => {
    const step = direction * this.widthStepNm;

    // Mid-draw: adjust the width of the segment currently being drawn (the one
    // leaving the last placed point) against the active layer.
    if (this.points.length > 0) {
      const layer = this.activeLayer;
      const current = layout.effectiveRouteWidth(this.currentWidth, layer);
      this.currentWidth = layout.normalizeRouteWidth(
        current + step,
        layer,
        this.widthStepNm
      );
      // Freeze the new width onto the in-progress segment's start point.
      this.pointWidths[this.points.length - 1] = this.currentWidth;
      this.updateStatusBar();
      return this.editor.requestRedraw();
    }

    // Idle: bump each segment of every selected route by one step.
    const selected = this.selectedRoutes();
    if (selected.length === 0) return null;

    const singles: Array<changes.Single<RouteEntity>> = [];
    for (const route of selected) {
      const oldPath = route.data.path;
      // Width lives on the segment's start point; the last point has no
      // outgoing segment, so leave it untouched.
      const newPath = oldPath.map((rp, i) =>
        i < oldPath.length - 1
          ? {
              ...rp,
              width: layout.normalizeRouteWidth(
                layout.effectiveRouteWidth(rp.width, rp.layer) + step,
                rp.layer,
                this.widthStepNm
              ),
            }
          : rp
      );
      const changed = newPath.some(
        (rp, i) => (rp.width ?? null) !== (oldPath[i].width ?? null)
      );
      if (!changed) continue; // fully clamped — no-op
      route.update({ path: newPath });
      singles.push(
        changes.update({
          target: route,
          from: { path: oldPath },
          to: { path: newPath },
        })
      );
    }
    if (singles.length === 1) {
      this.content.logChange(singles[0]);
    } else if (singles.length > 1) {
      const combo = changes.Combo.fromArray(singles);
      if (combo.ok) this.content.logChange(combo.val);
    }
    this.updateStatusBar();
    return this.editor.requestRedraw();
  };

  // The currently-selected routes, in selection order.
  private selectedRoutes = (): RouteEntity[] => {
    const routes: RouteEntity[] = [];
    for (const entity of this.content.selection) {
      if (entity instanceof RouteEntity) routes.push(entity);
    }
    return routes;
  };

  // Open the precise-width dialog for the route being drawn (mid-draw).
  private openWidthDialog = (): unknown => {
    const layer = this.activeLayer;
    const { min, max, default: def } = layout.routeWidthConstraints(layer);
    this.editor.platform.notifyEditRouteWidth?.({
      currentWidth: layout.effectiveRouteWidth(this.currentWidth, layer),
      minWidth: min,
      maxWidth: max,
      defaultWidth: def,
      manufacturingGridNm: this.widthStepNm,
    });
    return null;
  };

  // Called by the IDE when the user submits a width for the route being drawn.
  applyDrawWidth = (width: number | null): void => {
    this.currentWidth = width;
    // Apply to the in-progress segment (leaving the last placed point), if any.
    if (this.points.length > 0) {
      this.pointWidths[this.points.length - 1] = this.currentWidth;
    }
    this.updateStatusBar();
    this.editor.requestRedraw();
  };

  override abort = (): unknown => {
    // Drop any deferred route interaction.
    this.pendingRouteInteraction = null;
    // Mid-draw: discard in-progress stroke and stay in DrawRoute.
    // Idle (no points placed): exit route mode entirely.
    if (this.points.length > 0) {
      this.points.length = 0;
      this.pointLayers.length = 0;
      this.pointWidths.length = 0;
      this.gravityTarget = null;
      this.layerUserSelected = false;
      this.cursor = { ...this.editor.uiState.mousePos.sch };
      return this.editor.requestRedraw();
    }
    return this.cancel();
  };

  // Fill a translucent box previewing the drawn width of an axis-aligned
  // segment a→b. Diagonal segments (pre-snap rubber-band) get no box — the
  // centerline alone is drawn for those. Effective width falls back to the
  // active layer default when the segment width is null/undefined.
  private drawPreviewWidthBox = (
    to: DrawTo,
    a: model.Point,
    b: model.Point,
    width: number | null
  ): void => {
    const w = layout.effectiveRouteWidth(width, this.activeLayer);
    if (a.y === b.y && a.x !== b.x) {
      const xmin = Math.min(a.x, b.x);
      const xmax = Math.max(a.x, b.x);
      to.bbox(
        model.Bbox.fromTwoPoints([
          { x: xmax + w / 2, y: a.y + w / 2 },
          { x: xmin - w / 2, y: a.y - w / 2 },
        ]),
        previewWidthFillStyle
      );
    } else if (a.x === b.x && a.y !== b.y) {
      const ymin = Math.min(a.y, b.y);
      const ymax = Math.max(a.y, b.y);
      to.bbox(
        model.Bbox.fromTwoPoints([
          { x: a.x + w / 2, y: ymax + w / 2 },
          { x: a.x - w / 2, y: ymin - w / 2 },
        ]),
        previewWidthFillStyle
      );
    }
  };

  // Draw the in-progress route preview as an overlay
  override draw = (to: DrawTo): void => {
    if (this.points.length === 0) return;
    const scale = this.editor.canvas.worldTransforms.schToPage.scale;
    const scaledStyle = {
      ...previewLineStyle,
      linewidth: previewLineStyle.linewidth / scale,
    };

    // Draw all confirmed segments: a translucent fill at the segment's drawn
    // width (so per-segment widths are visible while routing) plus a thin
    // centerline for precision.
    for (let i = 0; i < this.points.length - 1; i++) {
      const a = this.points[i];
      const b = this.points[i + 1];
      this.drawPreviewWidthBox(to, a, b, this.pointWidths[i]);
      to.path(graphics.Path.fromPartial({ points: [a, b] }), scaledStyle);
    }

    // Draw rubber-band from last point to cursor (L-shape when gravity is
    // active), previewing the current draw width the same way.
    const last = this.points[this.points.length - 1];
    if (this.gravityTarget !== null) {
      // L-shaped preview: horizontal to bend, then vertical to pin grid point
      const bend = lBendPoint(last, this.gravityTarget);
      if (bend !== null) {
        this.drawPreviewWidthBox(to, last, bend, this.currentWidth);
        this.drawPreviewWidthBox(
          to,
          bend,
          this.gravityTarget,
          this.currentWidth
        );
        to.path(
          graphics.Path.fromPartial({ points: [last, bend] }),
          scaledStyle
        );
        to.path(
          graphics.Path.fromPartial({ points: [bend, this.gravityTarget] }),
          scaledStyle
        );
      } else {
        // Already on same horizontal line — single segment
        this.drawPreviewWidthBox(
          to,
          last,
          this.gravityTarget,
          this.currentWidth
        );
        to.path(
          graphics.Path.fromPartial({ points: [last, this.gravityTarget] }),
          scaledStyle
        );
      }
      drawPinSnapIndicator(to, this.gravityTarget, 8 / scale, scaledStyle);
    } else {
      this.drawPreviewWidthBox(to, last, this.cursor, this.currentWidth);
      to.path(
        graphics.Path.fromPartial({ points: [last, this.cursor] }),
        scaledStyle
      );
      drawCrosshair(to, this.cursor, 6 / scale, scaledStyle);
    }

    // Flying lines: semi-transparent lines from the route tip to connected pins,
    // colored per net so you can distinguish which targets belong to which net.
    if (this.showFlyingLines && this.flyingLineTargets.length > 0) {
      const tip = this.gravityTarget ?? this.cursor;
      for (let gi = 0; gi < this.flyingLineTargets.length; gi++) {
        const group = this.flyingLineTargets[gi];
        const [r, g, b] = FLYING_LINE_COLORS[gi % FLYING_LINE_COLORS.length];
        const flyStyle: style.ShapeStyle = {
          ...previewLineStyle,
          linewidth: 1 / scale,
          stroke: style.Color.new(r, g, b, 0.5),
          fill: style.Special.None,
        };
        for (const target of group.targets) {
          to.path(
            graphics.Path.fromPartial({ points: [tip, target] }),
            flyStyle
          );
          drawPinSnapIndicator(to, target, 6 / scale, flyStyle);
        }
      }
    }
  };

  // Delete all selected routes without resetting to idle mode.
  // Mirrors content.deleteSelected() but skips the goUiIdle() call so the
  // user stays in DrawRoute mode after deletion.
  private deleteSelectedRoutes = (): unknown => {
    const { content } = this;
    if (content.selection.size === 0) return null;
    const removals: Array<changes.Remove<RouteEntity>> = [];
    for (const entity of content.selection) {
      if (!(entity instanceof RouteEntity)) continue;
      if (content.removeEntity(entity, true)) {
        removals.push(changes.Remove.new(entity));
      }
    }
    if (removals.length === 0) return null;
    if (removals.length === 1) {
      content.logChange(removals[0]);
    } else {
      const combo = changes.Combo.fromArray(removals);
      if (combo.ok) content.logChange(combo.val);
    }
    return content.deselect();
  };

  // Snapshot all selected routes into the clipboard.
  private copySelectedRoutes = (): unknown => {
    const { content } = this;
    this.routeClipboard = [];
    for (const entity of content.selection) {
      if (!(entity instanceof RouteEntity)) continue;
      this.routeClipboard.push({
        net: entity.data.net,
        // Per-point width rides the spread; `locked` is a route-level attribute.
        path: entity.data.path.map((rp) => ({ ...rp, point: { ...rp.point } })),
        locked: entity.data.locked,
      });
    }
    return null;
  };

  // Paste clipboard routes at a fixed grid offset, then select the new routes.
  private pasteRoutes = (): unknown => {
    const { content, editor } = this;
    if (this.routeClipboard.length === 0) return null;

    // Offset each pasted route by 10 routing-grid pitches in X and Y so it
    // lands visibly near the original without overlapping it exactly.
    const offset = editor.grid.settings.snapX * 10;

    const adds: Array<changes.Single<RouteEntity>> = [];
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    for (const routeData of this.routeClipboard) {
      const newPath = routeData.path.map((rp) => ({
        ...rp,
        point: { x: rp.point.x + offset, y: rp.point.y + offset },
      }));
      const routeEntity = content.layoutEntity.createRoute({
        net: routeData.net,
        // newPath carries each point's per-segment width via the spread above.
        path: newPath,
        locked: routeData.locked,
      });
      adds.push(changes.Add.new(routeEntity));
      for (const rp of newPath) {
        minX = Math.min(minX, rp.point.x);
        minY = Math.min(minY, rp.point.y);
        maxX = Math.max(maxX, rp.point.x);
        maxY = Math.max(maxY, rp.point.y);
      }
    }

    if (adds.length === 1) {
      content.logChange(adds[0]);
    } else {
      const combo = changes.Combo.fromArray(adds);
      if (combo.ok) content.logChange(combo.val);
    }

    // Select the newly pasted routes.
    if (minX !== Infinity) {
      content.selectAllRoutesInBbox(
        model.Bbox.fromTwoPoints([
          { x: minX, y: minY },
          { x: maxX, y: maxY },
        ]),
        false
      );
    }

    return editor.requestRedraw();
  };

  // Cycle activeLayer up (+1) or down (-1) through the metal layer stack.
  // Mid-draw: insert a via (duplicate point, old layer → new layer) at the
  // current cursor position so the layer transition is physically recorded.
  private cycleLayer = (direction: 1 | -1): unknown => {
    const metals = this.content.stack?.metalLayers;
    if (!metals || metals.length < 2) return null;
    const curIdx = metals.findIndex(
      (l) =>
        l.layer === this.activeLayer.layer &&
        l.purpose === this.activeLayer.purpose
    );
    const nextLayer =
      metals[(curIdx + direction + metals.length) % metals.length];
    if (this.points.length > 0) {
      // Insert via: same cursor position twice, old layer → new layer.
      const via = { ...this.cursor };
      this.points.push(via);
      this.pointLayers.push(this.activeLayer); // end of old-layer run
      this.pointWidths.push(this.currentWidth);
      this.points.push({ ...via });
      this.pointLayers.push(nextLayer); // start of new-layer run
      this.pointWidths.push(this.currentWidth);
    }
    this.setActiveLayer(nextLayer);
    this.layerUserSelected = true;
    this.updateStatusBar();
    return this.editor.requestRedraw();
  };

  // Returns a human-readable label for a metal layer, e.g. "M1", "M2".
  // Uses the layer's position in the stack (1-based) since MetalLayer has no name field.
  layerLabel = (layer: layout.MetalLayer): string => {
    const metals = this.content.stack?.metalLayers;
    if (!metals) return "";
    const idx = metals.findIndex(
      (l) => l.layer === layer.layer && l.purpose === layer.purpose
    );
    return idx >= 0 ? `M${idx + 1}` : "";
  };

  // Switch to a specific metal layer (by index in the stack).
  // Mid-draw: inserts a via at the current cursor position (same as cycleLayer).
  // Called by the layer picker context menu.
  switchToLayerByIndex = (layerIdx: number): unknown => {
    const metals = this.content.stack?.metalLayers;
    if (!metals || layerIdx < 0 || layerIdx >= metals.length) return null;
    const nextLayer = metals[layerIdx];
    // Don't switch if already on this layer
    if (
      nextLayer.layer === this.activeLayer.layer &&
      nextLayer.purpose === this.activeLayer.purpose
    )
      return null;
    if (this.points.length > 0) {
      // Insert via: same cursor position twice, old layer → new layer.
      const via = { ...this.cursor };
      this.points.push(via);
      this.pointLayers.push(this.activeLayer);
      this.pointWidths.push(this.currentWidth);
      this.points.push({ ...via });
      this.pointLayers.push(nextLayer);
      this.pointWidths.push(this.currentWidth);
    }
    this.setActiveLayer(nextLayer);
    this.layerUserSelected = true;
    this.updateStatusBar();
    return this.editor.requestRedraw();
  };

  // Update the status bar to reflect the current active layer and width.
  private updateStatusBar = (): void => {
    const label = this.layerLabel(this.activeLayer);
    const prefix = label ? `Route Mode · ${label}` : "Route Mode";
    this.editor.overlays.setStatusMessage(
      `${prefix} · ${this.widthStatus()}`,
      "draw-route"
    );
  };

  // Width portion of the status message. Idle-with-selection shows the selected
  // route width(s); otherwise the pending draw width.
  private widthStatus = (): string => {
    const fmt = (w: layout.RouteWidth, layer: layout.MetalLayer): string => {
      const eff = layout.effectiveRouteWidth(w, layer);
      // One predicate for "is default" — see isDefaultRouteWidth.
      return layout.isDefaultRouteWidth(w) ? `${eff}nm` : `${eff}nm (custom)`;
    };

    if (this.points.length === 0) {
      const selected = this.selectedRoutes();
      // Width is per-segment; show the first segment of the first route. Guard
      // with length (index access is typed non-null but is undefined at runtime
      // when empty).
      if (selected.length > 0 && selected[0].data.path.length > 0) {
        const firstPt = selected[0].data.path[0];
        const count = selected.length > 1 ? ` (${selected.length} routes)` : "";
        return `Selected: ${fmt(firstPt.width, firstPt.layer)}${count}`;
      }
    }
    return `Width: ${fmt(this.currentWidth, this.activeLayer)}`;
  };

  // Commit the drawn route to the layout
  private commit = (): unknown => {
    const { editor, content, points, horizLayer, vertLayer } = this;

    // Need at least 2 points to form a segment — just reset and stay in mode
    if (points.length < 2) {
      points.length = 0;
      this.pointLayers.length = 0;
      this.pointWidths.length = 0;
      this.flyingLineTargets = [];
      this.setActiveLayer(vertLayer);
      this.layerUserSelected = false;
      this.updateStatusBar();
      return editor.requestRedraw();
    }

    // Build path: each RoutePoint carries the layer of its outgoing segment.
    // The last point inherits the incoming segment's layer (no outgoing segment).
    // If the user pressed `l` to switch layers, pointLayers records the explicit
    // layer at each via point; these propagate forward to subsequent null entries.
    // Without any layer switch, falls back to direction-based assignment.
    let explicitLayer: layout.MetalLayer | null = null;
    const path: layout.ModelRoutePoint[] = points.map((p, i) => {
      // Per-segment width from the parallel pointWidths array (width of the
      // segment leaving this point). The last point has no outgoing segment.
      const width = i < points.length - 1 ? this.pointWidths[i] : undefined;
      const pinned = this.pointLayers[i];
      if (pinned !== null) explicitLayer = pinned;
      if (explicitLayer !== null) {
        return { point: { ...p }, layer: explicitLayer, width };
      }
      // Direction-based fallback (no `l` press yet)
      const segIdx = Math.min(i, points.length - 2);
      const p0 = points[segIdx];
      const p1 = points[segIdx + 1];
      const layer = p0.y === p1.y ? horizLayer : vertLayer;
      return { point: { ...p }, layer, width };
    });

    // For direction-based routing (no manual layer switch via u/d), insert via
    // pairs at layer transitions so the renderer draws via markers at corners.
    let finalPath = path;
    if (!this.layerUserSelected) {
      const pathWithVias: layout.ModelRoutePoint[] = [];
      for (let i = 0; i < path.length; i++) {
        if (i > 0 && path[i].layer !== path[i - 1].layer) {
          // Duplicate the turn point with the incoming layer to form a via pair.
          pathWithVias.push({
            point: { ...path[i].point },
            layer: path[i - 1].layer,
          });
        }
        pathWithVias.push(path[i]);
      }
      finalPath = pathWithVias;
    }

    // Bug B3: if the start/end point lands on a pin one metal layer away, add a
    // junction via there so the route physically connects up/down to the pin.
    finalPath = this.addLandingVias(finalPath);

    // Per-segment widths were attached in the `path` map above and preserved
    // through the via insertions (inserted via points carry no wire width,
    // which is correct — a via's own slot is ignored).
    const routeData: layout.Route = {
      net: "",
      path: finalPath,
    };

    const routeEntity = content.layoutEntity.createRoute(routeData);
    content.logChange(changes.Add.new(routeEntity));

    // Stay in DrawRoute mode — reset points and layer state so the user can
    // draw the next route without re-entering the mode. Shift+R or Esc exits.
    this.points.length = 0;
    this.pointLayers.length = 0;
    this.pointWidths.length = 0;
    this.flyingLineTargets = [];
    this.setActiveLayer(vertLayer);
    this.layerUserSelected = false;
    this.updateStatusBar();
    this.cursor = { ...editor.uiState.mousePos.sch };
    return editor.requestRedraw();
  };

  // Add a junction via at the route's start/end when it lands on a pin whose
  // metal layer is exactly one layer away from the route's layer there (bug B3).
  // Basic scope: single-layer step only; a >= 2-layer gap (via stack) and
  // landing on a bare route (needs route-snap) are follow-ups, left unchanged.
  private addLandingVias = (
    path: layout.ModelRoutePoint[]
  ): layout.ModelRoutePoint[] => {
    const metals = this.content.stack?.metalLayers;
    if (!metals || path.length === 0) return path;
    const idxOf = (l: layout.MetalLayer): number =>
      metals.findIndex((m) => m.layer === l.layer && m.purpose === l.purpose);
    // One metal layer apart (and both resolvable in the stack).
    const oneApart = (a: layout.MetalLayer, b: layout.MetalLayer): boolean => {
      const ia = idxOf(a);
      const ib = idxOf(b);
      return ia >= 0 && ib >= 0 && Math.abs(ia - ib) === 1;
    };
    const result = [...path];

    // End: append a duplicate endpoint on the pin's layer → via pair.
    const end = result[result.length - 1];
    const endPin = pinLayerAtPoint(this.editor, this.content, end.point);
    if (endPin && oneApart(endPin, end.layer)) {
      result.push({ point: { ...end.point }, layer: endPin });
    }

    // Start: prepend a duplicate start point on the pin's layer → via pair.
    const start = result[0];
    const startPin = pinLayerAtPoint(this.editor, this.content, start.point);
    if (startPin && oneApart(startPin, start.layer)) {
      result.unshift({ point: { ...start.point }, layer: startPin });
    }

    return result;
  };

  private cancel = (): unknown => {
    this.cleanup();
    return this.editor.requestRedraw();
  };

  private cleanup = (): void => {
    this.editor.overlays.clearStatusMessage("draw-route");
    // Restore the grid settings that were active before routing mode was entered.
    this.editor.grid.updateSettings(this.savedGridSettings);
    this.editor.goUiIdle();
  };
}

//
// # MoveRoute
//
// Drag an existing route to a new position, snapped to the routing grid.
// Entered from DrawRoute mode (press R first, then click-drag a route).
// On commit or cancel, returns to the originating DrawRoute instance.
//
class MoveRoute extends ModeHandler {
  // Original path saved for undo/restore
  private readonly origPath: layout.Route["path"];

  private constructor(
    private readonly editor: InteractionHost,
    private readonly content: LayoutContent,
    private readonly route: RouteEntity,
    private readonly initialMousePos: model.Point,
    private readonly drawRoute: DrawRoute
  ) {
    super();
    this.origPath = route.data.path.map((rp) => ({
      ...rp,
      point: { ...rp.point },
    }));
    editor.overlays.setStatusMessage(
      "Move route: release to place, Esc to cancel",
      "move-route"
    );
  }

  static enter(
    editor: InteractionHost,
    content: LayoutContent,
    route: RouteEntity,
    drawRoute: DrawRoute
  ): MoveRoute {
    const mode = new MoveRoute(
      editor,
      content,
      route,
      { ...editor.uiState.mousePos.sch },
      drawRoute
    );
    editor.setMode(mode);
    return mode;
  }

  // Translate all route points by the grid-snapped delta from the initial mouse position.
  override handleMouseMove = (): unknown => {
    const { editor, origPath, initialMousePos, route } = this;
    const rawDelta = Point.sub(editor.uiState.mousePos.sch, initialMousePos);
    const snappedDelta = editor.grid.nearestPoint(rawDelta);
    const newPath = origPath.map((rp) => ({
      ...rp,
      point: {
        x: rp.point.x + snappedDelta.x,
        y: rp.point.y + snappedDelta.y,
      },
    }));
    return route.update({ path: newPath });
  };

  override handleMouseUp = (): unknown => this.commit();

  override handleKey = (e: KeyboardEvent): unknown => {
    if (e.key === Keys.Escape) return this.abort();
    return null;
  };

  override abort = (): unknown => {
    this.route.update({ path: this.origPath });
    this.cleanup();
    return this.editor.requestRedraw();
  };

  private commit = (): unknown => {
    const { content, route, origPath } = this;
    const currentPath = route.data.path;
    const moved = currentPath.some(
      (rp, i) =>
        rp.point.x !== origPath[i].point.x || rp.point.y !== origPath[i].point.y
    );
    if (moved) {
      content.logChange(
        changes.update({
          target: route,
          from: {
            path: origPath.map((rp) => ({ ...rp, point: { ...rp.point } })),
          },
          to: {
            path: currentPath.map((rp) => ({ ...rp, point: { ...rp.point } })),
          },
        })
      );
    }
    this.cleanup();
    return this.editor.requestRedraw();
  };

  private cleanup = (): void => {
    this.editor.overlays.clearStatusMessage("move-route");
    // Return to the DrawRoute mode that launched us.
    this.editor.setMode(this.drawRoute);
  };
}

//
// # MoveSegment
//
// Drag a single route segment perpendicular to its axis, keeping the route connected.
// Adjacent segments stretch or shrink automatically because they share the moved endpoints.
//
// Entered from DrawRoute on a plain left-click+drag on a segment.
// Shift+click enters MoveRoute instead (whole-route drag).
//
class MoveSegment extends ModeHandler {
  // Original path saved for undo/restore on Escape
  private readonly origPath: layout.Route["path"];

  private constructor(
    private readonly editor: InteractionHost,
    private readonly content: LayoutContent,
    private readonly route: RouteEntity,
    // Index of this segment in route.data.path (segment = path[segIdx]→path[segIdx+1])
    private readonly segIdx: number,
    private readonly segDir: "horiz" | "vert" | "other",
    private readonly initialMousePos: model.Point,
    private readonly drawRoute: DrawRoute
  ) {
    super();
    this.origPath = route.data.path.map((rp) => ({
      ...rp,
      point: { ...rp.point },
    }));
    const axis =
      segDir === "horiz"
        ? "up/down"
        : segDir === "vert"
        ? "left/right"
        : "freely";
    editor.overlays.setStatusMessage(
      `Drag segment ${axis} · Esc to cancel`,
      "move-segment"
    );
  }

  static enter(
    editor: InteractionHost,
    content: LayoutContent,
    route: RouteEntity,
    segIdx: number,
    segDir: "horiz" | "vert" | "other",
    drawRoute: DrawRoute
  ): MoveSegment {
    const mode = new MoveSegment(
      editor,
      content,
      route,
      segIdx,
      segDir,
      { ...editor.uiState.mousePos.sch },
      drawRoute
    );
    editor.setMode(mode);
    return mode;
  }

  // Rebuild the path from origPath each frame, moving the dragged segment
  // perpendicular to its axis. Via-aware: via pairs (same coordinate, different
  // layer) at the segment endpoints are treated as a unit and moved together.
  // When inserting bend points for collinear neighbors, new perpendicular
  // sub-segments get direction-based layers (with via pairs at transitions).
  override handleMouseMove = (): unknown => {
    const {
      editor,
      origPath,
      segIdx,
      segDir,
      route,
      initialMousePos,
      content,
    } = this;
    const rawDelta = Point.sub(editor.uiState.mousePos.sch, initialMousePos);
    const snappedDelta = editor.grid.nearestPoint(rawDelta);

    // Direction-based layers for new perpendicular sub-segments.
    const horizLayer = getLayerByDirection(content, "horiz");
    const vertLayer = getLayerByDirection(content, "vert");
    // The perpendicular layer: when dragging a horiz segment, new sub-segments are vert.
    const perpLayer =
      segDir === "horiz" ? vertLayer : segDir === "vert" ? horizLayer : null;

    // Helper: check if two path points form a via (same position, different layer).
    const isVia = (a: number, b: number): boolean => {
      if (a < 0 || b < 0 || a >= origPath.length || b >= origPath.length)
        return false;
      return (
        origPath[a].point.x === origPath[b].point.x &&
        origPath[a].point.y === origPath[b].point.y
      );
    };

    // Expand the segment range to include via pairs at each endpoint.
    let moveStart = segIdx;
    let moveEnd = segIdx + 1;
    while (moveStart > 0 && isVia(moveStart - 1, moveStart)) moveStart--;
    while (moveEnd < origPath.length - 1 && isVia(moveEnd, moveEnd + 1))
      moveEnd++;

    // Apply the perpendicular delta to a point.
    const movePoint = (pt: model.Point): model.Point => {
      if (segDir === "horiz") return { x: pt.x, y: pt.y + snappedDelta.y };
      if (segDir === "vert") return { x: pt.x + snappedDelta.x, y: pt.y };
      return { x: pt.x + snappedDelta.x, y: pt.y + snappedDelta.y };
    };

    const leftNeighborIdx = moveStart > 0 ? moveStart - 1 : -1;
    const rightNeighborIdx = moveEnd < origPath.length - 1 ? moveEnd + 1 : -1;

    const newPath: layout.Route["path"] = [];

    for (let i = 0; i < origPath.length; i++) {
      const rp = origPath[i];

      if (i === moveStart && leftNeighborIdx >= 0) {
        const leftPt = origPath[leftNeighborIdx].point;
        const isCollinear =
          (segDir === "horiz" && leftPt.y === rp.point.y) ||
          (segDir === "vert" && leftPt.x === rp.point.x);
        if (isCollinear && perpLayer) {
          // Insert bend: neighbor-layer point at original pos, then via to perp layer,
          // then perp-layer point at moved pos.
          const neighborLayer = origPath[leftNeighborIdx].layer;
          const bendPt = { ...rp.point };
          newPath.push({ layer: neighborLayer, point: bendPt });
          // Via pair at the bend (neighbor layer → perp layer)
          if (
            neighborLayer.layer !== perpLayer.layer ||
            neighborLayer.purpose !== perpLayer.purpose
          ) {
            newPath.push({ layer: perpLayer, point: { ...bendPt } });
          }
          // Via pair at the moved position (perp layer → segment layer)
          const movedPt = movePoint(rp.point);
          if (
            perpLayer.layer !== rp.layer.layer ||
            perpLayer.purpose !== rp.layer.purpose
          ) {
            newPath.push({ layer: perpLayer, point: { ...movedPt } });
          }
          newPath.push({ ...rp, point: movedPt });
        } else if (isCollinear) {
          newPath.push({
            ...origPath[leftNeighborIdx],
            point: { ...rp.point },
          });
          newPath.push({ ...rp, point: movePoint(rp.point) });
        } else {
          newPath.push({ ...rp, point: movePoint(rp.point) });
        }
      } else if (i > moveStart && i <= moveEnd) {
        newPath.push({ ...rp, point: movePoint(rp.point) });
        if (i === moveEnd && rightNeighborIdx >= 0) {
          const rightPt = origPath[rightNeighborIdx].point;
          const isCollinear =
            (segDir === "horiz" && rightPt.y === rp.point.y) ||
            (segDir === "vert" && rightPt.x === rp.point.x);
          if (isCollinear && perpLayer) {
            const neighborLayer = origPath[rightNeighborIdx].layer;
            const movedPt = movePoint(rp.point);
            const bendPt = { ...rp.point };
            // Via pair at the moved position (segment layer → perp layer)
            if (
              rp.layer.layer !== perpLayer.layer ||
              rp.layer.purpose !== perpLayer.purpose
            ) {
              newPath.push({ layer: perpLayer, point: { ...movedPt } });
            }
            // Via pair at the bend (perp layer → neighbor layer)
            if (
              perpLayer.layer !== neighborLayer.layer ||
              perpLayer.purpose !== neighborLayer.purpose
            ) {
              newPath.push({ layer: perpLayer, point: { ...bendPt } });
            }
            newPath.push({ layer: neighborLayer, point: bendPt });
          } else if (isCollinear) {
            newPath.push({
              ...origPath[rightNeighborIdx],
              point: { ...rp.point },
            });
          }
        }
      } else {
        newPath.push({ ...rp, point: { ...rp.point } });
      }
    }

    return route.update({ path: newPath });
  };

  override handleMouseUp = (): unknown => this.commit();

  override handleKey = (e: KeyboardEvent): unknown => {
    if (e.key === Keys.Escape) return this.abort();
    return null;
  };

  override abort = (): unknown => {
    this.route.update({ path: this.origPath });
    this.cleanup();
    return this.editor.requestRedraw();
  };

  private commit = (): unknown => {
    const { content, route, origPath } = this;
    const currentPath = route.data.path;
    const moved = currentPath.some(
      (rp, i) =>
        rp.point.x !== origPath[i].point.x || rp.point.y !== origPath[i].point.y
    );
    if (moved) {
      content.logChange(
        changes.update({
          target: route,
          from: {
            path: origPath.map((rp) => ({ ...rp, point: { ...rp.point } })),
          },
          to: {
            path: currentPath.map((rp) => ({ ...rp, point: { ...rp.point } })),
          },
        })
      );
    }
    this.cleanup();
    return this.editor.requestRedraw();
  };

  private cleanup = (): void => {
    this.editor.overlays.clearStatusMessage("move-segment");
    this.editor.setMode(this.drawRoute);
  };
}

//
// # ExtendRouteEnd
//
// Drag a route endpoint along its terminal segment's axis to extend or shrink it.
// Entered from DrawRoute when the user clicks near the first or last point of a route.
// The endpoint moves along the segment's direction, snapped to the routing grid.
// On commit, logs an Update change. On Escape, restores the original path.
//
class ExtendRouteEnd extends ModeHandler {
  private readonly origPath: layout.Route["path"];

  private constructor(
    private readonly editor: InteractionHost,
    private readonly content: LayoutContent,
    private readonly route: RouteEntity,
    // "start" = path[0], "end" = path[path.length - 1]
    private readonly which: "start" | "end",
    // Axis of the terminal segment
    private readonly axis: "horiz" | "vert",
    private readonly initialMousePos: model.Point,
    private readonly drawRoute: DrawRoute
  ) {
    super();
    this.origPath = route.data.path.map((rp) => ({
      ...rp,
      point: { ...rp.point },
    }));
    const dir = axis === "horiz" ? "left/right" : "up/down";
    editor.overlays.setStatusMessage(
      `Extend ${which} ${dir} · Esc to cancel`,
      "extend-route-end"
    );
  }

  static enter(
    editor: InteractionHost,
    content: LayoutContent,
    route: RouteEntity,
    which: "start" | "end",
    axis: "horiz" | "vert",
    drawRoute: DrawRoute
  ): ExtendRouteEnd {
    const mode = new ExtendRouteEnd(
      editor,
      content,
      route,
      which,
      axis,
      { ...editor.uiState.mousePos.sch },
      drawRoute
    );
    editor.setMode(mode);
    return mode;
  }

  override handleMouseMove = (): unknown => {
    const { editor, origPath, route, which, axis, initialMousePos } = this;
    const rawDelta = Point.sub(editor.uiState.mousePos.sch, initialMousePos);
    const snappedDelta = editor.grid.nearestPoint(rawDelta);

    const newPath = origPath.map((rp) => ({
      ...rp,
      point: { ...rp.point },
    }));

    // Move only the endpoint along the terminal segment's axis
    const idx = which === "start" ? 0 : newPath.length - 1;
    if (axis === "horiz") {
      newPath[idx].point.x += snappedDelta.x;
    } else {
      newPath[idx].point.y += snappedDelta.y;
    }

    return route.update({ path: newPath });
  };

  override handleMouseUp = (): unknown => this.commit();

  override handleKey = (e: KeyboardEvent): unknown => {
    if (e.key === Keys.Escape) return this.abort();
    return null;
  };

  override abort = (): unknown => {
    this.route.update({ path: this.origPath });
    this.cleanup();
    return this.editor.requestRedraw();
  };

  private commit = (): unknown => {
    const { content, route, origPath } = this;
    const currentPath = route.data.path;
    const moved = currentPath.some(
      (rp, i) =>
        rp.point.x !== origPath[i].point.x || rp.point.y !== origPath[i].point.y
    );
    if (moved) {
      content.logChange(
        changes.update({
          target: route,
          from: {
            path: origPath.map((rp) => ({ ...rp, point: { ...rp.point } })),
          },
          to: {
            path: currentPath.map((rp) => ({
              ...rp,
              point: { ...rp.point },
            })),
          },
        })
      );
    }
    this.cleanup();
    return this.editor.requestRedraw();
  };

  private cleanup = (): void => {
    this.editor.overlays.clearStatusMessage("extend-route-end");
    this.editor.setMode(this.drawRoute);
  };
}

// Threshold in screen pixels: if a click is within this distance of a route
// endpoint, treat it as an endpoint extend rather than a segment drag.
const ENDPOINT_SNAP_PX = 15;

// Check if a click is near a route endpoint. Returns "start", "end", or null.
function hitTestRouteEndpoint(
  editor: InteractionHost,
  route: RouteEntity
): {
  which: "start" | "end";
  axis: "horiz" | "vert";
} | null {
  const { path } = route.data;
  if (path.length < 2) return null;

  const scale = editor.canvas.worldTransforms.schToPage.scale;
  const r2 = (ENDPOINT_SNAP_PX / scale) ** 2;
  const mouse = editor.uiState.mousePos.sch;

  // Check start endpoint
  const startPt = path[0].point;
  const dxS = mouse.x - startPt.x;
  const dyS = mouse.y - startPt.y;
  if (dxS * dxS + dyS * dyS <= r2) {
    // Terminal segment is path[0] → path[1]
    const seg1 = path[1].point;
    const axis: "horiz" | "vert" =
      Math.abs(seg1.x - startPt.x) >= Math.abs(seg1.y - startPt.y)
        ? "horiz"
        : "vert";
    return { which: "start", axis };
  }

  // Check end endpoint
  const endPt = path[path.length - 1].point;
  const dxE = mouse.x - endPt.x;
  const dyE = mouse.y - endPt.y;
  if (dxE * dxE + dyE * dyE <= r2) {
    // Terminal segment is path[-2] → path[-1]
    const seg2 = path[path.length - 2].point;
    const axis: "horiz" | "vert" =
      Math.abs(endPt.x - seg2.x) >= Math.abs(endPt.y - seg2.y)
        ? "horiz"
        : "vert";
    return { which: "end", axis };
  }

  return null;
}

//
// # MoveRouteGroup
//
// Drag a set of selected routes together as a group, translating all of them
// by the same grid-snapped delta. Entered from DrawRoute when the user clicks
// a route that is already part of a multi-route selection.
// On commit, logs a single Combo change covering all moved routes.
// On Escape / abort, restores all original paths.
//
class MoveRouteGroup extends ModeHandler {
  // Original paths saved for undo/restore on Escape
  private readonly origPaths: Map<RouteEntity, layout.Route["path"]>;

  private constructor(
    private readonly editor: InteractionHost,
    private readonly content: LayoutContent,
    routes: RouteEntity[],
    private readonly initialMousePos: model.Point,
    private readonly drawRoute: DrawRoute
  ) {
    super();
    this.origPaths = new Map(
      routes.map((r) => [
        r,
        r.data.path.map((rp) => ({ ...rp, point: { ...rp.point } })),
      ])
    );
    editor.overlays.setStatusMessage(
      `Moving ${this.origPaths.size} routes · Esc to cancel`,
      "move-route-group"
    );
  }

  static enter(
    editor: InteractionHost,
    content: LayoutContent,
    routes: RouteEntity[],
    drawRoute: DrawRoute
  ): MoveRouteGroup {
    const mode = new MoveRouteGroup(
      editor,
      content,
      routes,
      { ...editor.uiState.mousePos.sch },
      drawRoute
    );
    editor.setMode(mode);
    return mode;
  }

  // Translate all routes by the grid-snapped delta from the initial mouse position.
  override handleMouseMove = (): unknown => {
    const { editor, origPaths, initialMousePos } = this;
    const rawDelta = Point.sub(editor.uiState.mousePos.sch, initialMousePos);
    const snappedDelta = editor.grid.nearestPoint(rawDelta);
    for (const [route, origPath] of origPaths) {
      route.update({
        path: origPath.map((rp) => ({
          ...rp,
          point: {
            x: rp.point.x + snappedDelta.x,
            y: rp.point.y + snappedDelta.y,
          },
        })),
      });
    }
    return editor.requestRedraw();
  };

  override handleMouseUp = (): unknown => this.commit();

  override handleKey = (e: KeyboardEvent): unknown => {
    if (e.key === Keys.Escape) return this.abort();
    return null;
  };

  override abort = (): unknown => {
    for (const [route, origPath] of this.origPaths) {
      route.update({ path: origPath });
    }
    this.cleanup();
    return this.editor.requestRedraw();
  };

  private commit = (): unknown => {
    const { content, origPaths } = this;
    const updates: Array<changes.Single<RouteEntity>> = [];
    for (const [route, origPath] of origPaths) {
      const currentPath = route.data.path;
      const moved = currentPath.some(
        (rp, i) =>
          rp.point.x !== origPath[i].point.x ||
          rp.point.y !== origPath[i].point.y
      );
      if (moved) {
        updates.push(
          changes.update({
            target: route,
            from: {
              path: origPath.map((rp) => ({ ...rp, point: { ...rp.point } })),
            },
            to: {
              path: currentPath.map((rp) => ({
                ...rp,
                point: { ...rp.point },
              })),
            },
          })
        );
      }
    }
    if (updates.length === 1) {
      content.logChange(updates[0]);
    } else if (updates.length > 1) {
      const combo = changes.Combo.fromArray(updates);
      if (combo.ok) content.logChange(combo.val);
    }
    this.cleanup();
    return this.editor.requestRedraw();
  };

  private cleanup = (): void => {
    this.editor.overlays.clearStatusMessage("move-route-group");
    this.editor.setMode(this.drawRoute);
  };
}

//
// # RouteBoxSelect
//
// Drag-based box select operating within DrawRoute mode.
// Selects routes (not instances) whose segments intersect the drawn rectangle.
// A plain click (no drag) produces a zero-size bbox → no hits → deselects all.
// On completion, returns to the originating DrawRoute instance (not idle).
//
const ROUTE_BOX_FILL = style.Color.new(100, 150, 255, 0.15);
const ROUTE_BOX_STROKE = style.Color.new(100, 150, 255, 0.8);
const routeBoxStyle: style.ShapeStyle = {
  cap: "round",
  join: "round",
  linewidth: 1,
  fill: ROUTE_BOX_FILL,
  stroke: ROUTE_BOX_STROKE,
};

class RouteBoxSelect extends ModeHandler {
  private endpoint: model.Point;

  private constructor(
    private readonly editor: InteractionHost,
    private readonly content: LayoutContent,
    private readonly drawRoute: DrawRoute,
    private readonly anchor: model.Point,
    private readonly additive: boolean
  ) {
    super();
    this.endpoint = { ...anchor };
  }

  static enter(
    editor: InteractionHost,
    content: LayoutContent,
    drawRoute: DrawRoute,
    anchor: model.Point,
    additive: boolean
  ): RouteBoxSelect {
    const mode = new RouteBoxSelect(
      editor,
      content,
      drawRoute,
      { ...anchor },
      additive
    );
    editor.setMode(mode);
    return mode;
  }

  override handleMouseMove = (): unknown => {
    this.endpoint = { ...this.editor.uiState.mousePos.sch };
    return this.editor.requestRedraw();
  };

  override handleMouseUp = (): unknown => {
    this.finalize();
    return null;
  };

  override abort = (): unknown => {
    this.editor.setMode(this.drawRoute);
    return this.editor.requestRedraw();
  };

  override draw = (to: DrawTo): void => {
    const { anchor, endpoint } = this;
    const scale = this.editor.canvas.worldTransforms.schToPage.scale;
    const bbox: model.Bbox = {
      min: {
        x: Math.min(anchor.x, endpoint.x),
        y: Math.min(anchor.y, endpoint.y),
      },
      max: {
        x: Math.max(anchor.x, endpoint.x),
        y: Math.max(anchor.y, endpoint.y),
      },
    };
    to.bbox(bbox, {
      ...routeBoxStyle,
      linewidth: routeBoxStyle.linewidth / scale,
    });
  };

  private finalize = (): void => {
    const { editor, content, anchor, endpoint, additive, drawRoute } = this;
    const bbox = model.Bbox.fromTwoPoints([anchor, endpoint]);
    content.selectAllRoutesInBbox(bbox, additive);
    // Return to DrawRoute mode (not idle).
    editor.setMode(drawRoute);
  };
}

//
// # AutoRoute
//
// Two-click A-to-B auto-routing sub-mode of DrawRoute.
// Press `A` while idle in DrawRoute to enter.
// First click: snap to nearest pin as source (pin gravity active).
// Second click: snap to nearest pin as destination; commits an H-then-V single-bend
// route as a normal Route entity with full undo support.
// Escape returns to DrawRoute without committing.
//
// Only H-then-V single-bend routing is implemented. Multi-bend / obstacle-avoiding
// routing is future work.
//
class AutoRoute extends ModeHandler {
  // Source point, set on first click. Null until the first click.
  private source: model.Point | null = null;
  // Current cursor position, updated on mouse move (pin-gravity applied).
  private cursor: model.Point;
  // True when cursor is currently snapped to a pin via gravity.
  private isGravitySnapped = false;

  private constructor(
    private readonly editor: InteractionHost,
    private readonly content: LayoutContent,
    private readonly drawRoute: DrawRoute
  ) {
    super();
    this.cursor = { ...editor.uiState.mousePos.sch };
    editor.overlays.setStatusMessage(
      "Auto-route: click source pin · Esc to cancel",
      "auto-route"
    );
  }

  static enter(
    editor: InteractionHost,
    content: LayoutContent,
    drawRoute: DrawRoute
  ): AutoRoute {
    const mode = new AutoRoute(editor, content, drawRoute);
    editor.setMode(mode);
    return mode;
  }

  // Update cursor with pin gravity on every mouse move.
  override handleMouseMove = (): unknown => {
    const { editor, content } = this;
    const rawSnapped = editor.grid.nearestPoint(editor.uiState.mousePos.sch);
    const gravity = snapWithPinGravity(editor, content, rawSnapped);
    this.isGravitySnapped = gravity !== null;
    this.cursor = gravity ?? rawSnapped;
    return editor.requestRedraw();
  };

  override handleMouseUp = (): unknown => {
    const { editor, content } = this;
    const rawSnapped = editor.grid.nearestPoint(editor.uiState.mousePos.sch);
    const gravity = snapWithPinGravity(editor, content, rawSnapped);
    const clicked = gravity ?? rawSnapped;

    if (this.source === null) {
      // First click: store source and wait for destination.
      this.source = { ...clicked };
      editor.overlays.setStatusMessage(
        "Auto-route: click destination pin · Esc to cancel",
        "auto-route"
      );
      return editor.requestRedraw();
    }

    // Second click: auto-route from source to destination and commit.
    return this.commitRoute(clicked);
  };

  override handleKey = (e: KeyboardEvent): unknown => {
    if (e.key === Keys.Escape) return this.abort();
    return null;
  };

  override abort = (): unknown => {
    this.cleanup();
    return this.editor.requestRedraw();
  };

  // Draw rubber-band preview showing the planned H-then-V path.
  override draw = (to: DrawTo): void => {
    const scale = this.editor.canvas.worldTransforms.schToPage.scale;
    const scaledStyle = {
      ...previewLineStyle,
      linewidth: previewLineStyle.linewidth / scale,
    };
    const markerSize = 8 / scale;

    // Always show a pin-snap indicator (or crosshair) at the cursor.
    if (this.isGravitySnapped) {
      drawPinSnapIndicator(to, this.cursor, markerSize, scaledStyle);
    } else {
      drawCrosshair(to, this.cursor, 6 / scale, scaledStyle);
    }

    if (this.source === null) return;

    // Show a pin-snap indicator at the locked source.
    drawPinSnapIndicator(to, this.source, markerSize, scaledStyle);

    // Draw H-then-V rubber-band preview from source to cursor.
    const { source, cursor } = this;
    if (source.y === cursor.y) {
      // Already horizontal — single segment.
      to.path(
        graphics.Path.fromPartial({ points: [source, cursor] }),
        scaledStyle
      );
    } else if (source.x === cursor.x) {
      // Already vertical — single segment.
      to.path(
        graphics.Path.fromPartial({ points: [source, cursor] }),
        scaledStyle
      );
    } else {
      // H-then-V bend: go horizontal to cursor.x, then vertical to cursor.y.
      const bend = { x: cursor.x, y: source.y };
      to.path(
        graphics.Path.fromPartial({ points: [source, bend] }),
        scaledStyle
      );
      to.path(
        graphics.Path.fromPartial({ points: [bend, cursor] }),
        scaledStyle
      );
    }
  };

  // Build the route path between two points using an H-then-V single-bend strategy.
  // When source and dest share a row or column, a direct single-segment path is used.
  // A layer-transition via (duplicate point with both layer IDs) is inserted at the
  // bend when the route changes axis.
  private buildPath(src: model.Point, dest: model.Point): layout.Route["path"] {
    const horizLayer = getLayerByDirection(this.content, "horiz");
    const vertLayer = getLayerByDirection(this.content, "vert");
    if (!horizLayer || !vertLayer) return [];

    if (src.y === dest.y) {
      // Purely horizontal.
      return [
        { point: { ...src }, layer: horizLayer },
        { point: { ...dest }, layer: horizLayer },
      ];
    }

    if (src.x === dest.x) {
      // Purely vertical.
      return [
        { point: { ...src }, layer: vertLayer },
        { point: { ...dest }, layer: vertLayer },
      ];
    }

    // H-then-V: go horizontal first, then vertical.
    // A via is inserted at the bend (duplicate point with old→new layer).
    const bend = { x: dest.x, y: src.y };
    return [
      { point: { ...src }, layer: horizLayer },
      { point: { ...bend }, layer: horizLayer }, // end of horizontal run
      { point: { ...bend }, layer: vertLayer }, // via: start of vertical run
      { point: { ...dest }, layer: vertLayer },
    ];
  }

  private commitRoute = (dest: model.Point): unknown => {
    const { editor, content } = this;
    const src = this.source!;

    // If source and destination are the same point, nothing to commit.
    if (src.x === dest.x && src.y === dest.y) {
      this.cleanup();
      return editor.requestRedraw();
    }

    const path = this.buildPath(src, dest);
    if (path.length < 2) {
      // No valid layers available — exit silently.
      this.cleanup();
      return editor.requestRedraw();
    }

    const routeData: layout.Route = { net: "", path };
    const routeEntity = content.layoutEntity.createRoute(routeData);
    content.logChange(changes.Add.new(routeEntity));

    this.cleanup();
    return editor.requestRedraw();
  };

  private cleanup = (): void => {
    this.editor.overlays.clearStatusMessage("auto-route");
    this.editor.setMode(this.drawRoute);
  };
}
